import { useEffect } from 'react';
import useGuideStore from '../store/useGuideStore';
import type { GuideIndex, GuideDocument } from '../types/guide';

// ─── Load the guide index on app mount ───────────────────────────────────────

export function useGuideIndex() {
  const { setIndex, setIndexLoading, setIndexError } = useGuideStore();

  useEffect(() => {
    const load = async () => {
      setIndexLoading(true);
      setIndexError(null);
      try {
        const res = await fetch('/guides/index.json');
        if (!res.ok) throw new Error(`Failed to load guide index (${res.status})`);
        const data: GuideIndex = await res.json();
        setIndex(data.guides);
      } catch (err) {
        setIndexError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setIndexLoading(false);
      }
    };
    load();
  }, [setIndex, setIndexLoading, setIndexError]);
}

// ─── Load a specific guide by its file path ───────────────────────────────────

export function useGuide(file: string | null) {
  const { setActiveGuide, setGuideLoading, setGuideError } = useGuideStore();

  useEffect(() => {
    if (!file) return;

    const load = async () => {
      setGuideLoading(true);
      setGuideError(null);
      setActiveGuide(null);
      try {
        const res = await fetch(`/guides/${file}`);
        if (!res.ok) throw new Error(`Failed to load guide (${res.status})`);
        const data: GuideDocument = await res.json();
        setActiveGuide(data);
      } catch (err) {
        setGuideError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setGuideLoading(false);
      }
    };
    load();
  }, [file, setActiveGuide, setGuideLoading, setGuideError]);
}
